import React, {useEffect, useRef, useState} from "react";

import {
    Alert,
    Button,
    Card,
    DatePicker,
    Empty,
    Flex,
    Form,
    Input,
    message,
    Modal,
    Popconfirm,
    Segmented,
    Select,
    Spin,
    Tag,
    Typography,
    Upload,
} from "antd";
import Apiurl from "../ConstURL";

import {
    DeleteOutlined,
    DownloadOutlined,
    FileTextOutlined,
    InboxOutlined,
} from '@ant-design/icons';

import axios from "axios";
import {format} from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

const {Text, Title} = Typography;

const REPORTS = {
    orders: {
        type: "orders",
        logtype: "IMPORTDATA",
        endpoint: "/shop",
        title: "Отчёт по заказам",
        hint: "Стандартный TXT-отчёт по заказам Amazon (поля amazon-order-id, purchase-date, order-status, item-price).",
        accept: ".txt,.tsv,.csv",
        color: "purple",
    },
    refunds: {
        type: "refunds",
        logtype: "IMPORTREFUNDS",
        endpoint: "/shop/refunds",
        title: "Отчёт по возвратам",
        hint: "TSV-отчёт по возвратам (Return Reports): поля Order ID, Order date, Refunded Amount.",
        accept: ".tsv,.txt,.csv",
        color: "volcano",
    },
    listings: {
        type: "listings",
        logtype: "IMPORTLISTINGS",
        endpoint: "/shop/listings",
        title: "Листинги (All Listings)",
        hint: "Отчёт All Listings Report (.txt) — один файл на магазин за день (поля seller-sku, asin1, quantity, status). Если за этот магазин и день отчёт уже есть, программа спросит, заменить ли его. Сравнение с предыдущим днём формируется автоматически — раздел «Проверка листингов».",
        accept: ".txt,.tsv",
        color: "cyan",
        single: true,
        dated: true,
    },
};

dayjs.extend(utc);

// Даты отчётов хранятся как полночь UTC — берём дату из строки, без сдвига часового пояса.
const fmtReportDay = (iso) => (iso && !iso.startsWith("0001") ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "");
// «Сегодня» — по Москве, как считает сервер.
const todayMskStr = () => dayjs().utcOffset(180).format("YYYY-MM-DD");
const todayMskDay = () => dayjs(todayMskStr());
const fmtMsk = (iso) => dayjs(iso).utcOffset(180).format("DD.MM.YYYY HH:mm");

const uploadErrorText = (err) => {
    const status = err?.response?.status;
    if (status === 413) return "Файл слишком большой для сервера (ограничение на размер загрузки).";
    if (status === 502 || status === 504) return "Сервер не успел ответить. Через минуту проверьте историю загрузок — отчёт мог загрузиться.";
    return err?.response?.data?.Message || "Ошибка загрузки";
};

export default function ImportData() {
    const [form] = Form.useForm();

    const [reportType, setReportType] = useState("orders");
    const [fileList, setFileList] = useState([]);
    const [loading, setloading] = useState(false);
    const [selectedshopid, set_selectedshopid] = useState("");
    const [filename_current, Set_filename] = useState("");

    const [allshops, set_allshops] = useState([]);
    const [shopshistory, set_shopshistory] = useState([]);

    const cfg = REPORTS[reportType];
    const historyReq = useRef(0);
    const reportTypeRef = useRef(reportType);
    reportTypeRef.current = reportType;
    // День (МСК), в который дату выбрали вручную: в этот день «сегодня» сами не подставляем.
    const userPickedDate = useRef("");
    const [checking, setChecking] = useState(false);
    const [modal, modalContext] = Modal.useModal();
    const reportDate = Form.useWatch("reportdate", form);

    useEffect(() => {
        axios.get(Apiurl + "/shop?token=" + localStorage.getItem("token")).then((res) => {
            set_allshops(res.data.Data || []);
        }).catch((err) => console.log(err));
    }, []);

    // Если вкладка была открыта со вчера, а дату никто не выбирал вручную, — ставим сегодняшнюю,
    // иначе сегодняшний отчёт молча уйдёт во вчерашний день.
    useEffect(() => {
        const refreshDate = () => {
            if (document.visibilityState === "visible" && form.getFieldValue("reportdate") && userPickedDate.current !== todayMskStr()) {
                form.setFieldsValue({reportdate: todayMskDay()});
            }
        };
        document.addEventListener("visibilitychange", refreshDate);
        window.addEventListener("focus", refreshDate);
        return () => {
            document.removeEventListener("visibilitychange", refreshDate);
            window.removeEventListener("focus", refreshDate);
        };
    }, [form]);

    const loadHistory = (shopid, type, fileFilter = "") => {
        // Ответы могут прийти не по порядку — показываем только последний запрос.
        const seq = ++historyReq.current;
        if (!shopid) {
            set_shopshistory([]);
            return;
        }
        axios.get(Apiurl + "/shop/logs?" + new URLSearchParams({
            token: localStorage.getItem("token"),
            id: shopid,
            logtype: REPORTS[type].logtype,
            filelog: fileFilter,
        })).then((res) => {
            if (seq === historyReq.current && res.status === 200) set_shopshistory(res.data.Data || []);
        }).catch((err) => {
            console.log(err);
            if (seq === historyReq.current) {
                // Не оставляем на экране историю другого магазина с кнопками «Удалить».
                set_shopshistory([]);
                message.error("Не удалось загрузить историю импорта");
            }
        });
    };

    const beforeUpload = (file, list) => {
        const exts = cfg.accept.split(",");
        const name = (file.name || "").toLowerCase();
        if (!exts.some((e) => name.endsWith(e))) {
            message.error(`${file.name}: нужен файл ${exts.join(" / ")}`);
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    // При перетаскивании Upload молча отбрасывает лишние и неподходящие файлы — говорим об этом.
    const onDropFiles = (e) => {
        const dropped = Array.from(e.dataTransfer?.files || []);
        const exts = cfg.accept.split(",");
        const bad = dropped.filter((f) => !exts.some((x) => (f.name || "").toLowerCase().endsWith(x)));
        if (bad.length > 0) message.error(`${bad.map((f) => f.name).join(", ")}: нужен файл ${exts.join(" / ")}`);
        if (cfg.single && dropped.length - bad.length > 1) message.warning("Загружается один файл на магазин — взят первый.");
    };

    const normFile = (e) => {
        if (Array.isArray(e)) return [...e.fileList];
        const newFileList = [...e.fileList];
        if (e.fileList.length > 0) {
            newFileList.forEach((item, index) => {
                newFileList[index]["status"] = "done";
            });
        }
        return newFileList;
    };

    const onChangeupload = (info) => setFileList([...info.fileList]);

    const DeleteLog = (logid) => {
        axios.delete(Apiurl + "/shop/log?" + new URLSearchParams({
            token: localStorage.getItem("token"),
            logid: logid,
        })).then((res) => {
            if (res.status === 200) {
                message.success("Файл удалён!");
                loadHistory(selectedshopid, reportType, filename_current);
            } else {
                message.error("Ошибка в отправке запроса!");
            }
        }).catch((err) => console.log(err));
    };

    const onChange_filename = (e) => {
        const text = e.target.value;
        Set_filename(text);
        loadHistory(selectedshopid, reportType, text);
    };

    const onChangeReport = (value) => {
        setReportType(value);
        setFileList([]);
        form.resetFields(["dragger"]);
        Set_filename("");
        loadHistory(selectedshopid, value, "");
    };

    const uploadFiles = (values, replace) => {
        const data = new FormData();
        values.dragger.forEach((item, index) => {
            data.append('taskfile' + index, item.originFileObj);
        });
        const config = {
            headers: {"content-type": 'multipart/form-data; boundary=----WebKitFormBoundaryqTqJIxvkWFYqvP5s'},
        };
        const params = {
            token: localStorage.getItem("token"),
            id: values.shopid,
        };
        if (cfg.dated) {
            params.date = values.reportdate.format("YYYY-MM-DD");
            if (replace) params.replace = "1";
        }
        setloading(true);
        axios.put(Apiurl + cfg.endpoint + "?" + new URLSearchParams(params), data, config).then((res) => {
            setloading(false);
            if (res.data.Status) {
                message.success(cfg.dated && res.data.Message ? res.data.Message : "Импорт завершён!", cfg.dated ? 10 : 4);
                form.resetFields(["dragger"]);
                setFileList([]);
            } else {
                message.error("База не обновлена! Ошибка файла: " + res.data.Message, 15);
            }
            set_selectedshopid(values.shopid);
            // Тип отчёта могли переключить, пока шла загрузка, — показываем историю текущего.
            loadHistory(values.shopid, reportTypeRef.current, "");
        }).catch((err) => {
            setloading(false);
            if (err?.response?.status === 409 && err.response.data?.NeedConfirm) {
                confirmReplace(err.response.data.Message, values);
                return;
            }
            message.error(uploadErrorText(err), 12);
            console.log(err);
        });
    };

    const confirmReplace = (text, values) => {
        modal.confirm({
            title: "Заменить отчёт?",
            content: text,
            okText: "Заменить",
            okButtonProps: {danger: true},
            cancelText: "Отмена",
            onOk: () => uploadFiles(values, true),
        });
    };

    const Sendrequest = (values) => {
        if (!cfg.dated) {
            uploadFiles(values, false);
            return;
        }
        // Сначала спрашиваем, нет ли уже отчёта за этот магазин и день, — чтобы не заменить его по ошибке.
        if (checking) return;
        setChecking(true);
        const date = values.reportdate.format("YYYY-MM-DD");
        axios.get(Apiurl + "/shop/listings/exists?" + new URLSearchParams({
            token: localStorage.getItem("token"),
            id: values.shopid,
            date: date,
        })).then((res) => {
            if (res.data.Exists) {
                const shop = allshops.find((s) => s.Id === values.shopid)?.Name || "";
                confirmReplace(`За ${fmtReportDay(date)} по магазину ${shop} уже загружен «${res.data.Filenameold}» (${fmtMsk(res.data.TimeCreated)} МСК). Заменить его новым файлом?`, values);
            } else {
                uploadFiles(values, false);
            }
        }).catch(() => uploadFiles(values, false)) // сервер всё равно спросит подтверждение (409)
            .finally(() => setChecking(false));
    };

    return (
        <div style={{padding: "24px 28px"}}>
            <Flex vertical align="center" gap="24px">
                <Card
                    style={{width: "100%", maxWidth: 720, borderRadius: 16, border: "1px solid #2a2140"}}
                    styles={{body: {padding: 28}}}
                >
                    <Flex vertical gap="6px" style={{marginBottom: 18}}>
                        <Title level={4} style={{margin: 0}}>Импорт данных</Title>
                        <Text type="secondary">
                            {cfg.dated
                                ? "Выберите магазин, дату отчёта и загрузите файл."
                                : "Выберите магазин и загрузите файл отчёта. Период определяется автоматически по датам внутри отчёта."}
                        </Text>
                    </Flex>

                    {modalContext}
                    <Spin spinning={loading} tip="Загрузка и обработка файла...">
                    <Segmented
                        block
                        value={reportType}
                        disabled={loading || checking}
                        onChange={onChangeReport}
                        options={[
                            {label: REPORTS.orders.title, value: "orders"},
                            {label: REPORTS.refunds.title, value: "refunds"},
                            {label: REPORTS.listings.title, value: "listings"},
                        ]}
                        style={{marginBottom: 16}}
                    />

                    <Alert
                        type="info"
                        showIcon
                        message={cfg.hint}
                        style={{marginBottom: 20}}
                    />

                        <Form
                            layout="vertical"
                            form={form}
                            initialValues={{remember: true}}
                            onFinish={Sendrequest}
                            autoComplete="off"
                        >
                            <Form.Item
                                label="Магазин (маркетплейс)"
                                name="shopid"
                                rules={[{required: true, message: 'Выберите магазин!'}]}
                            >
                                <Select
                                    placeholder="Выберите магазин"
                                    showSearch
                                    optionFilterProp="label"
                                    onChange={(e) => {
                                        set_selectedshopid(e);
                                        Set_filename("");
                                        loadHistory(e, reportType, "");
                                    }}
                                    options={allshops.map((s) => ({value: s.Id, label: s.Name}))}
                                />
                            </Form.Item>

                            {cfg.dated && (
                                <Form.Item
                                    label="Дата отчёта"
                                    name="reportdate"
                                    initialValue={todayMskDay()}
                                    rules={[{required: true, message: 'Выберите дату отчёта!'}]}
                                    extra={reportDate && reportDate.format("YYYY-MM-DD") !== todayMskStr()
                                        ? <Text type="warning">Отчёт будет загружен за {reportDate.format("DD.MM.YYYY")} — это не сегодняшняя дата.</Text>
                                        : null}
                                >
                                    <DatePicker
                                        format="DD.MM.YYYY"
                                        allowClear={false}
                                        onChange={() => (userPickedDate.current = todayMskStr())}
                                        disabledDate={(d) => d && d.format("YYYY-MM-DD") > todayMskStr()}
                                        style={{width: 200}}
                                    />
                                </Form.Item>
                            )}

                            <Form.Item label={`Файл (${cfg.title.toLowerCase()})`}>
                                <Form.Item
                                    name="dragger"
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                    rules={[{required: true, message: "Загрузите файл отчёта!"}]}
                                    noStyle
                                >
                                    <Upload.Dragger
                                        maxCount={cfg.single ? 1 : 30}
                                        name="files"
                                        fileList={fileList}
                                        accept={cfg.accept}
                                        onChange={onChangeupload}
                                        beforeUpload={beforeUpload}
                                        onDrop={onDropFiles}
                                        multiple={!cfg.single}
                                        customRequest={(info) => setFileList([info.file])}
                                    >
                                        <p className="ant-upload-drag-icon"><InboxOutlined/></p>
                                        <p className="ant-upload-text">
                                            {cfg.single ? "Нажмите или перетащите файл сюда" : "Нажмите или перетащите файлы сюда"}
                                        </p>
                                        <p className="ant-upload-hint" style={{fontSize: 12}}>
                                            Поддерживаются {cfg.accept.split(",").join(", ")}
                                        </p>
                                    </Upload.Dragger>
                                </Form.Item>
                            </Form.Item>

                            <Form.Item style={{marginBottom: 0}}>
                                <Button type="primary" htmlType="submit" size="large" block loading={checking || loading}>
                                    Импортировать
                                </Button>
                            </Form.Item>
                        </Form>
                    </Spin>
                </Card>

                {/* История */}
                <Card
                    style={{width: "100%", maxWidth: 720, borderRadius: 16, border: "1px solid #2a2140"}}
                    styles={{body: {padding: 24}}}
                    title={
                        <Flex align="center" gap="10px">
                            <span>История импорта</span>
                            <Tag color={cfg.color}>{cfg.title}</Tag>
                        </Flex>
                    }
                >
                    {selectedshopid !== "" && (
                        <Input
                            style={{marginBottom: 16, maxWidth: 280}}
                            placeholder="Поиск по названию файла"
                            allowClear
                            value={filename_current}
                            onChange={onChange_filename}
                        />
                    )}

                    {shopshistory.length === 0 ? (
                        <Empty description={selectedshopid ? "Нет загрузок" : "Выберите магазин"}/>
                    ) : (
                        <Flex gap="12px" vertical>
                            {shopshistory.map((history) => (
                                <Card key={history.Id} size="small"
                                      style={{border: "1px solid #2a2140", borderRadius: 12}}>
                                    <Flex justify="space-between" align="flex-start" gap="12px" wrap="wrap">
                                        <div style={{minWidth: 0}}>
                                            <Flex align="center" gap="8px" style={{marginBottom: 6}}>
                                                <FileTextOutlined/>
                                                <Text strong ellipsis style={{maxWidth: 360}}>
                                                    {history.Filenameold}
                                                </Text>
                                            </Flex>
                                            <Flex gap="6px" wrap="wrap" style={{marginBottom: 6}}>
                                                <Tag>{history.AllCountLines} строк</Tag>
                                                {reportType === "listings" && history.ReportDate && (
                                                    <Tag color="cyan">за {fmtReportDay(history.ReportDate)}</Tag>
                                                )}
                                                {history.Purged && <Tag>файл удалён по сроку хранения</Tag>}
                                            </Flex>
                                            <Text type="secondary" style={{fontSize: 12}}>
                                                {format(new Date(history.TimeCreated), 'dd.MM.yyyy HH:mm:ss')}
                                            </Text>
                                        </div>
                                        <Flex gap="8px">
                                            {!history.Purged && <Button
                                                icon={<DownloadOutlined/>}
                                                size="small"
                                                rel="noreferrer"
                                                target="_blank"
                                                href={Apiurl + "/shop/logs/get/file?" + new URLSearchParams({
                                                    token: localStorage.getItem("token"),
                                                    logid: history.Id,
                                                })}
                                            >
                                                Файл
                                            </Button>}
                                            <Popconfirm
                                                title="Удалить файл и его данные?"
                                                description={reportType === "listings"
                                                    ? "Сравнения, в которых участвовал этот отчёт, пересчитаются при ближайшей проверке листингов."
                                                    : "Данные этого файла пропадут из дашбордов и выгрузок."}
                                                okText="Удалить"
                                                okButtonProps={{danger: true}}
                                                cancelText="Отмена"
                                                onConfirm={() => DeleteLog(history.Id)}
                                            >
                                                <Button danger size="small" icon={<DeleteOutlined/>}>
                                                    Удалить
                                                </Button>
                                            </Popconfirm>
                                        </Flex>
                                    </Flex>
                                </Card>
                            ))}
                        </Flex>
                    )}
                </Card>
            </Flex>
        </div>
    );
}
